import type { KAPLAYCtx } from "kaplay";

export interface VictorySequenceState {
  active: boolean;
  timer: number;
  completed: boolean;
}

export function createVictorySequence(k: KAPLAYCtx, player: any) {
  // Add a flag to the player object to indicate victory mode
  player.inVictorySequence = false;
  const state: VictorySequenceState = {
    active: false,
    timer: 0,
    completed: false,
  };

  let chargeSound: any = null;

  let pulseEffect: any = null;

  const start = () => {
    state.active = true;
    state.timer = 0;
    state.completed = false;

    // Play charge sound at the very beginning
    chargeSound = k.play("charge", { volume: 0.5 });

    // Disable gravity but keep the player active for rotation
    player.gravityScale = 0;
    player.vel.y = 0; // Stop current fall

    // Stage 1: Camera movement (0-1.5s) - Use Kaplay's tween for smooth camera movement
    const startCam = k.getCamPos();
    const targetCam = k.vec2(player.pos.x, player.pos.y);

    // Use tween for smooth camera movement to center on player
    k.tween(
      startCam,
      targetCam,
      1.5,
      (pos) => k.setCamPos(pos),
      k.easings.easeOutCubic,
    );

    // Stage 2: Pulse effect (1.5-2.5s)
    k.wait(1.5, () => {
      // Stop charge sound and play woosh
      if (chargeSound) {
        chargeSound.stop();
        chargeSound = null;
      }
      k.play("whoosh", { volume: 0.9 });

      // Create pulse effect
      pulseEffect = k.add([
        k.circle(0),
        k.pos(player.pos),
        k.color(255, 255, 0),
        k.opacity(1),
        k.z(100),
        k.animate(),
      ]);

      // Animate pulse expansion and fade
      pulseEffect.animate("radius", [0, k.width()], {
        duration: 1.0,
        easing: k.easings.easeOutQuad,
      });

      pulseEffect.animate("opacity", [1, 0], {
        duration: 1.0,
        easing: k.easings.easeOutQuad,
      });

      // Stage 3: Complete (after 1.0s of pulse = 2.5s total)
      k.wait(1.0, () => {
        k.play("success", { volume: 0.7 });
        if (pulseEffect) pulseEffect.destroy();
        // Don't set completion here, let the update function handle it
      });
    });
  };

  const update = (dt: number): boolean => {
    if (!state.active) return false;

    state.timer += dt;

    // Check if we've completed the sequence (2.5 seconds total)
    if (state.timer >= 2.5 && !state.completed) {
      state.completed = true;
      state.active = false;
      return true; // Signal completion
    }

    return false; // Still running
  };

  const reset = () => {
    state.active = false;
    state.timer = 0;
    state.completed = false;

    // Stop charge sound if still playing
    if (chargeSound) {
      chargeSound.stop();
      chargeSound = null;
    }

    // Reset player physics
    player.gravityScale = 1;
  };

  return {
    start,
    update,
    reset,
    isActive: () => state.active,
    isCompleted: () => state.completed,
  };
}
